import { useEffect, useState } from "react";
import { Check, Lock, Users, Clock, Camera, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { SKIN_TEASERS, FREE_SKINS, COMING_SOON_SKINS, isSkinReleased, formatSkinRelease } from "../lib/theme";
import { useTheme } from "../context/ThemeContext";
import SkinPreview from "../components/SkinPreview";
import ServicePicker from "../components/ServicePicker";
import { COUNTRIES } from "../lib/regions";
import AvatarCropper from "../components/AvatarCropper";
import Avatar from "../components/Avatar";

export default function Settings() {
  const { user, patchUser, logout } = useAuth();
  const { skin, setSkin } = useTheme();
  const [svcMeta, setSvcMeta] = useState({ services: [], grouped: false, region_label: "United States" });
  const [selected, setSelected] = useState(new Set(user?.services || []));
  const [saving, setSaving] = useState(false);
  const [regionSaving, setRegionSaving] = useState(false);
  const [reqSkin, setReqSkin] = useState(null);
  const [reqSent, setReqSent] = useState([]);
  const fullAccess = !!user?.full_access;
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [profile, setProfile] = useState({ name: user?.name || "", mood: user?.mood || "", visibility: user?.visibility || "friends_only" });
  const [profSaving, setProfSaving] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [avatarBusy, setAvatarBusy] = useState(false);

  const onPickAvatar = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Please choose an image under 5 MB."); return; }
    setCropFile(f);
  };

  const uploadAvatar = async (blob) => {
    setCropFile(null);
    setAvatarBusy(true);
    const fd = new FormData();
    fd.append("file", blob, "avatar.jpg");
    try {
      const { data } = await api.post("/user/avatar", fd);
      patchUser(data);
      toast.success("Profile picture updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not upload picture.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      const { data } = await api.delete("/user/avatar");
      patchUser(data);
      toast("Profile picture removed");
    } catch {
      toast.error("Could not remove picture.");
    } finally {
      setAvatarBusy(false);
    }
  };

  const saveProfile = async () => {
    setProfSaving(true);
    try {
      const { data } = await api.put("/user/profile", { name: profile.name, mood: profile.mood, visibility: profile.visibility });
      patchUser(data);
      toast.success("Profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save profile.");
    } finally {
      setProfSaving(false);
    }
  };

  const redeem = async () => {
    if (!code.trim()) { toast.error("Enter an invite code."); return; }
    setRedeeming(true);
    try {
      const { data } = await api.post("/redeem-code", { code: code.trim() });
      patchUser(data.user || { full_access: true });
      toast.success("Full Access unlocked!");
      setCode("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not redeem code.");
    } finally {
      setRedeeming(false);
    }
  };

  const changePassword = async () => {
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match."); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password updated.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update password.");
    } finally {
      setPwSaving(false);
    }
  };

  useEffect(() => {
    const region = user?.region || "US";
    api.get("/meta/services", { params: { region } }).then(({ data }) => setSvcMeta(data)).catch(() => {});
  }, [user?.region]);

  const changeRegion = async (code) => {
    setRegionSaving(true);
    try {
      const { data } = await api.put("/user/region", { region: code });
      patchUser(data);
      toast.success("Region updated");
    } catch {
      toast.error("Could not update region.");
    } finally {
      setRegionSaving(false);
    }
  };

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const chooseSkin = (s) => {
    const unreleased = !isSkinReleased(s.id) && !user?.is_admin;
    if (unreleased) { toast.info(`${s.label} unlocks ${formatSkinRelease(s.id)}`); return; }
    const locked = !FREE_SKINS.includes(s.id) && !fullAccess;
    if (locked) { setReqSkin(s); return; }
    setSkin(s.id);
    toast.success(`${s.label} skin applied`);
  };

  const doRequest = async () => {
    try {
      await api.post("/access/request", { skin: reqSkin?.id });
      setReqSent((p) => [...p, reqSkin.id]);
      toast.success("Request sent — we'll review it shortly.");
      setReqSkin(null);
    } catch {
      toast.error("Could not send request. Try again.");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/user/services", { services: Array.from(selected) });
      patchUser({ services: data.services });
      toast.success("Your services were updated");
    } catch {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 pt-8 pb-20" data-testid="settings-page">
        <h1 className="font-head text-4xl sm:text-5xl tracking-wide text-[var(--text-primary)]">Settings</h1>

        <div className="mt-8 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Account</h2>
          <div className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <p className="text-[var(--text-secondary)]">Name <span className="ml-2 text-[var(--text-primary)]">{user?.name}</span></p>
            <p className="text-[var(--text-secondary)]">Email <span className="ml-2 text-[var(--text-primary)]">{user?.email}</span></p>
          </div>
          <button onClick={logout} className="mt-5 rounded-[var(--card-radius)] border border-[var(--border-accent)] px-5 py-2 text-sm font-semibold text-[var(--accent-hover)] hover:bg-[var(--accent)]/10" data-testid="settings-logout-button">
            Log Out
          </button>
          {user?.is_admin && (
            <Link to="/admin/users" className="mt-5 ml-3 inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-5 py-2 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid="settings-user-management-link">
              <Users size={16} /> User Management
            </Link>
          )}
        </div>

        <div className="mt-6 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6" data-testid="settings-profile">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Profile</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Your picture is private — only you see it. It shows up across every skin.</p>

          <div className="mt-5 flex flex-col gap-5 sm:flex-row sm:items-center">
            <div className="relative flex-none">
              <Avatar user={user} size={96} className="border-2 border-[var(--border-accent)]" testid="profile-avatar" />
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" data-testid="profile-avatar-upload-label">
                <Camera size={16} /> {user?.avatar_url ? "Replace photo" : "Upload photo"}
                <input type="file" accept="image/*" className="hidden" onChange={onPickAvatar} disabled={avatarBusy} data-testid="profile-avatar-input" />
              </label>
              {user?.avatar_url && (
                <button onClick={removeAvatar} disabled={avatarBusy} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)] disabled:opacity-60" data-testid="profile-avatar-remove">
                  <Trash2 size={16} /> Remove
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-[var(--text-secondary)]">Display name</span>
              <input value={profile.name} maxLength={60} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} className="w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="profile-name-input" placeholder="Your name" />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-[var(--text-secondary)]">Mood</span>
              <input value={profile.mood} maxLength={140} onChange={(e) => setProfile((p) => ({ ...p, mood: e.target.value }))} className="w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="profile-mood-input" placeholder="watching everything ✨" />
            </label>
          </div>
          <div className="mt-4">
            <span className="mb-1.5 block text-sm font-medium text-[var(--text-secondary)]">Profile visibility</span>
            <select value={profile.visibility} onChange={(e) => setProfile((p) => ({ ...p, visibility: e.target.value }))} className="w-full max-w-md rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="profile-visibility-select">
              <option value="everyone">Everyone — anyone can view my profile</option>
              <option value="friends_only">Friends only — only friends see my Top 8 &amp; favorites</option>
              <option value="private">Private — nobody but me</option>
            </select>
            <p className="mt-1 text-xs text-[var(--text-muted)]">Your profile lives at /profile/{user?.username || "your-handle"}. Profiles only show your pic, name, mood, Top 8 &amp; favorites — never watch history or settings.</p>
          </div>
          <button onClick={saveProfile} disabled={profSaving} className="mt-4 rounded-[var(--card-radius)] bg-[var(--accent)] px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="profile-save-button">
            {profSaving ? "Saving…" : "Save profile"}
          </button>
        </div>

        <div className="mt-6 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6" data-testid="settings-appearance">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Interface Skin</h2>
            <span className="rounded-full border border-[var(--border-accent)] bg-[var(--accent)]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[var(--accent-hover)]" data-testid="access-tier-badge">{fullAccess ? "Full Access" : "Free Tier"}</span>
          </div>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Reskin The Dial to a different streaming era — each restyles the whole interface.{!fullAccess && " Classic and Early 2010s are free; the rest need Full Access."}</p>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {SKIN_TEASERS.map((s) => {
              const isActive = skin === s.id;
              const comingSoon = !isSkinReleased(s.id) && !user?.is_admin;
              const locked = !comingSoon && !FREE_SKINS.includes(s.id) && !fullAccess;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => chooseSkin(s)}
                  className={`group relative overflow-hidden rounded-[var(--card-radius)] border p-3 text-left transition-all ${
                    isActive
                      ? "border-[var(--accent)] ring-1 ring-[var(--accent)]"
                      : comingSoon
                      ? "border-dashed border-[var(--border-subtle)] opacity-70 cursor-not-allowed"
                      : locked
                      ? "border-[var(--border-subtle)] opacity-80 hover:border-[var(--accent-hover)]"
                      : "border-[var(--border-subtle)] hover:border-[var(--accent)]"
                  }`}
                  data-testid={`skin-teaser-${s.id}`}
                >
                  <div className="relative">
                    <SkinPreview skinId={s.id} />
                    {comingSoon ? (
                      <div className="absolute inset-0 flex items-center justify-center rounded-sm bg-black/55 backdrop-blur-[1px]">
                        <Clock size={20} className="text-white" />
                      </div>
                    ) : locked ? (
                      <div className="absolute inset-0 flex items-center justify-center rounded-sm bg-black/45 backdrop-blur-[1px]">
                        <Lock size={20} className="text-white" />
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <span className="font-head text-lg text-[var(--text-primary)]">{s.label}</span>
                    {isActive && <Check size={15} className="text-[var(--accent)]" />}
                  </div>
                  <p className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">{s.era}</p>

                  {comingSoon ? (
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[var(--text-muted)]/25 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]" data-testid={`skin-teaser-${s.id}-coming-soon`}>
                      <Clock size={10} /> Coming Soon — {formatSkinRelease(s.id)}
                    </span>
                  ) : isActive ? (
                    <span className="mt-2 inline-block rounded bg-[var(--accent)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--accent-contrast)]" data-testid={`skin-teaser-${s.id}-active`}>
                      Selected
                    </span>
                  ) : locked ? (
                    <span className="mt-2 inline-flex items-center gap-1 rounded bg-[var(--text-muted)]/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]" data-testid={`skin-teaser-${s.id}-locked`}>
                      <Lock size={10} /> Full Access
                    </span>
                  ) : (
                    <span className="mt-2 inline-block rounded border border-[var(--border-accent)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--accent-hover)]" data-testid={`skin-teaser-${s.id}-available`}>
                      {!isSkinReleased(s.id) && user?.is_admin ? "Preview" : "Apply"}
                    </span>
                  )}
                </button>
              );
            })}
            {COMING_SOON_SKINS.map((s) => (
              <div key={s.id} aria-disabled className="relative cursor-not-allowed overflow-hidden rounded-[var(--card-radius)] border border-dashed border-[var(--border-subtle)] p-3 opacity-70" data-testid={`skin-coming-soon-${s.id}`}>
                <div className="flex aspect-[16/10] items-center justify-center rounded-sm border border-dashed border-[var(--border-subtle)] bg-[var(--bg-card)]">
                  <Clock size={22} className="text-[var(--text-muted)]" />
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                  <span className="font-head text-lg text-[var(--text-secondary)]">{s.label}</span>
                </div>
                <p className="text-[11px] uppercase tracking-wider text-[var(--text-muted)]">{s.era}</p>
                <span className="mt-2 inline-block rounded-full bg-[var(--text-muted)]/25 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]" data-testid={`skin-coming-soon-badge-${s.id}`}>
                  Coming Soon
                </span>
              </div>
            ))}
          </div>

          {reqSkin && (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--accent)]/10 p-4" data-testid="request-access-panel">
              <p className="text-sm text-[var(--text-primary)]"><span className="font-semibold">{reqSkin.label}</span> is a Full Access skin. Want it unlocked?</p>
              <div className="flex items-center gap-2">
                {reqSent.includes(reqSkin.id) ? (
                  <span className="text-sm font-semibold text-[var(--accent-hover)]" data-testid="request-access-sent">Request sent ✓</span>
                ) : (
                  <button onClick={doRequest} className="rounded-[var(--card-radius)] bg-[var(--accent)] px-4 py-2 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" data-testid="request-access-button">Request Full Access</button>
                )}
                <button onClick={() => setReqSkin(null)} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="request-access-dismiss">Dismiss</button>
              </div>
            </div>
          )}
        </div>

        <div id="settings-region" className="mt-6 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6 scroll-mt-20" data-testid="settings-region">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">My region</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Streaming catalogs differ by country. Set your region so Where to Watch and your service list show accurate listings.</p>
          <select
            value={user?.region || "US"}
            onChange={(e) => changeRegion(e.target.value)}
            disabled={regionSaving}
            className="mt-4 w-full max-w-md rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)] disabled:opacity-60"
            data-testid="settings-region-select"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="mt-6 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6" id="subscriptions">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Your Subscriptions</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Only select services you currently pay for. This drives "New on Your Services" and the Where to Watch links.</p>

          <div className="mt-5">
            <ServicePicker services={svcMeta.services} grouped={svcMeta.grouped} selected={selected} toggle={toggle} testidPrefix="settings-service-tile" heightClass="h-20" />
          </div>

          <p className="mt-4 text-xs text-[var(--text-muted)]" data-testid="settings-services-region-note">
            {`Services are shown for ${svcMeta.region_label} — `}
            <a href="#settings-region" className="font-semibold text-[var(--accent-hover)] underline" data-testid="settings-change-region-link">change region</a>
          </p>

          <button onClick={save} disabled={saving}
            className="mt-6 rounded-[var(--card-radius)] bg-[var(--accent)] px-8 py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60"
            data-testid="settings-save-button">
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>

        {!fullAccess && (
          <div className="mt-6 rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--accent)]/5 p-6" data-testid="redeem-code-card">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Have an invite code?</h2>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">Redeem a code to unlock Full Access and all 7 era skins instantly.</p>
            <div className="mt-4 flex max-w-md flex-wrap gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && redeem()} placeholder="ENTER CODE"
                className="min-w-[180px] flex-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm uppercase tracking-wider text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="redeem-code-input" />
              <button onClick={redeem} disabled={redeeming} className="rounded-[var(--card-radius)] bg-[var(--accent)] px-6 py-2.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="redeem-code-button">
                {redeeming ? "Redeeming…" : "Redeem"}
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-6" data-testid="change-password-card">          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Change Password</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Update the password for your account ({user?.email}).</p>
          <div className="mt-5 grid max-w-md gap-3">
            <input type="password" value={pw.current} onChange={(e) => setPw((s) => ({ ...s, current: e.target.value }))} placeholder="Current password" className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="pw-current" />
            <input type="password" value={pw.next} onChange={(e) => setPw((s) => ({ ...s, next: e.target.value }))} placeholder="New password (min 6 chars)" className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="pw-next" />
            <input type="password" value={pw.confirm} onChange={(e) => setPw((s) => ({ ...s, confirm: e.target.value }))} placeholder="Confirm new password" className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="pw-confirm" />
            <button onClick={changePassword} disabled={pwSaving} className="justify-self-start rounded-[var(--card-radius)] bg-[var(--accent)] px-6 py-2.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="pw-submit">
              {pwSaving ? "Updating…" : "Update Password"}
            </button>
          </div>
        </div>
      </main>
      {cropFile && <AvatarCropper file={cropFile} onCancel={() => setCropFile(null)} onCropped={uploadAvatar} />}
    </div>
  );
}
